import { User } from "@/types/user";
import request from ".";
import { CommonListParams } from "../types/common";
import { Org, OrgAdd, OrgUpdate } from "../types/org";
import { isArray, isOrg, isString, isStringEmpty } from "../utils/checkTool";
import { cleanToOrg } from "../utils/cleanData";

const ORG_URL = '/wms/org';

const ORG_APIS = {
    add: ORG_URL + `/add`,
    list: ORG_URL + `/list`,
    fetchs: ORG_URL + `/fetchs`,
    fetchIds: ORG_URL + `/fetch`,
    disabledList: ORG_URL + `/disable/list`,
    disabledFetchs: ORG_URL + `/disable/fetchs`,
    disable: (id: string) => ORG_URL + `/${id}/disable`,
    enable: (id: string) => ORG_URL + `/${id}/enable`,
    update: (id: string) => ORG_URL + `/${id}/update`,
    updateName: (id: string) => ORG_URL + `/${id}/name/update`,
    updatePid: (id: string) => ORG_URL + `/${id}/pid/update`,
    updateAvatar: (id: string) => ORG_URL + `/${id}/avatar/update`,
    updateDesc: (id: string) => ORG_URL + `/${id}/desc/update`,
    addOrgUsers: (id: string) => ORG_URL + `/${id}/users/add`,
    disableUser: (id: string) => ORG_URL + `/${id}/users/disable`,
    fetchOrgUsers: (id: string) => ORG_URL + `/${id}/users`,
}

export const orgAdd = (org: OrgAdd) => {
    if (!isString(org.name) || (org.pId && !isString(org.pId)) || (org.avatar && !isString(org.avatar)) || (org.desc && !isString(org.desc))) {
        return Promise.reject(10012)
    }
    return request<Org, OrgAdd>(ORG_APIS.add, { params: { body: org } })
        .then(({ body: res }) => {
            if (isOrg(res)) {
                return Promise.resolve(cleanToOrg(res))
            } else {
                return Promise.reject(10013)
            }
        })
        .catch(err => Promise.reject(err))
}

export const orgList = (params?: CommonListParams, supplierApi?: string) => {
    if (params && params.body && !isString(params.body)) {
        return Promise.reject(10012)
    }
    return request<Array<Org>, string>(ORG_APIS.list, { params, requestConfig: supplierApi ? { baseURL: supplierApi } : undefined })
        .then(res => {
            if (isArray<Org>(res.body)) {
                return Promise.resolve({
                    body: res.body.filter(res => isOrg(res))?.map(res => cleanToOrg(res)),
                    pagination: res.pagination
                })
            } else {
                return Promise.reject(10013)
            }
        })
        .catch(err => Promise.reject(err))
}

export const orgFetchs = (params?: CommonListParams) => {
    if (params && params.body && !isString(params.body)) {
        return Promise.reject(10012)
    }
    return request<Array<Org>, string>(ORG_APIS.fetchs, { params })
        .then(res => {
            if (isArray<Org>(res.body)) {
                return Promise.resolve({
                    body: res.body.filter(res => isOrg(res))?.map(res => cleanToOrg(res)),
                    pagination: res.pagination
                })
            } else {
                return Promise.reject(10013)
            }
        })
        .catch(err => Promise.reject(err))
}

export const orgDisabledList = (params?: CommonListParams) => {
    if (params && params.body && !isString(params.body)) {
        return Promise.reject(10012)
    }
    return request<Array<Org>, string>(ORG_APIS.disabledList, { params })
        .then(({ body: orgs }) => {
            if (isArray<Org>(orgs)) {
                return Promise.resolve(orgs.filter(org => isOrg(org))?.map(org => cleanToOrg(org)))
            } else {
                return Promise.reject(10013)
            }
        })
        .catch(err => Promise.reject(err))
}

export const orgDisabledFetchs = (params?: CommonListParams) => {
    if (params && params.body && !isString(params.body)) {
        return Promise.reject(10012)
    }
    return request<Array<Org>, string>(ORG_APIS.disabledFetchs, { params })
        .then(({ body: orgs }) => {
            if (isArray<Org>(orgs)) {
                return Promise.resolve(orgs.filter(org => isOrg(org))?.map(org => cleanToOrg(org)))
            } else {
                return Promise.reject(10013)
            }
        })
        .catch(err => Promise.reject(err))
}

export const orgFetchIds = (idsArr: Array<string>) => {
    if (!isArray(idsArr)) {
        return Promise.reject(10012)
    }
    return request<Array<Org>, Array<string>>(ORG_APIS.fetchIds, { params: { body: idsArr } })
        .then(({ body: orgs }) => {
            if (isArray<Org>(orgs)) {
                return orgs.filter(res => isOrg(res))?.map(res => cleanToOrg(res))
            } else {
                return Promise.reject(10013)
            }
        })
        .catch(err => Promise.reject(err))
}

export const orgDisable = (id: string) => {
    if (!isString(id) || isStringEmpty(id)) {
        return Promise.reject(10012)
    }
    return request<void>(ORG_APIS.disable(id))
}

export const orgEnable = (id: string) => {
    if (!isString(id) || isStringEmpty(id)) {
        return Promise.reject(10012)
    }
    return request<void>(ORG_APIS.enable(id))
}

export const orgUpdateName = (id: string, name: string) => {
    if ((!isString(id) || isStringEmpty(id)) && (name && !isString(name))) {
        return Promise.reject(10012)
    }
    return request<void>(ORG_APIS.updateName(id))
}

export const orgUpdateAvatar = (id: string, avatar: string) => {
    if ((!isString(id) || isStringEmpty(id)) && (avatar && !isString(avatar))) {
        return Promise.reject(10012)
    }
    return request<void>(ORG_APIS.updateAvatar(id))
}

export const orgUpdatePid = (id: string, pId: string) => {
    if ((!isString(id) || isStringEmpty(id)) && (pId && !isString(pId))) {
        return Promise.reject(10012)
    }
    return request<void, string>(ORG_APIS.updatePid(id), { params: { body: pId ? pId : undefined } })
}

export const orgUpdateDesc = (id: string, desc: string) => {
    if ((!isString(id) || isStringEmpty(id)) && (desc && !isString(desc))) {
        return Promise.reject(10012)
    }
    return request<void>(ORG_APIS.updateDesc(id))
}

export const orgUpdate = (id: string, info: OrgUpdate) => {
    if (!isString(id) || (isStringEmpty(id) &&
        (info.name && !isString(info.name)) &&
        (info.avatar && !isString(info.avatar)) &&
        (info.desc && !isString(info.desc)) &&
        (info.pId && !isString(info.pId)))) {
        return Promise.reject(10012)
    }

    return request<Org, OrgUpdate>(ORG_APIS.update(id), { params: { body: info } })
        .then(({ body: info }) => {
            if (isOrg(info)) {
                return Promise.resolve(cleanToOrg(info))
            } else {
                return Promise.reject(10013)
            }
        })
        .catch(err => Promise.reject(err))

}

export const orgAddOrgUsers = (id: string, userIdArray: Array<string>) => {
    if ((!isString(id) || (isStringEmpty(id) && isArray(userIdArray)))) {
        return Promise.reject(10012)
    }
    return request<Org, Array<string>>(ORG_APIS.addOrgUsers(id), { params: { body: userIdArray } })
        .then(({ body: res }) => {
            if (isOrg(res)) {
                return Promise.resolve(cleanToOrg(res))
            } else {
                return Promise.reject(10013)
            }
        })
        .catch(err => Promise.reject(err))
}

export const orgDisableUser = (id: string, usersIdArr: Array<string>) => {
    if ((!isString(id) || (isStringEmpty(id) && isArray(usersIdArr)))) {
        return Promise.reject(10012)
    }
    return request<void, Array<string>>(ORG_APIS.disableUser(id), { params: { body: usersIdArr } })
}

export const fetchOrgUsers = (id: string, params?: CommonListParams) => {
    if (!isString(id) || isStringEmpty(id) || (params && params.body && !isString(params.body))) {
        return Promise.reject(10012)
    }
    return request<Array<User>, string>(ORG_APIS.fetchOrgUsers(id), { params })
}
